import React, { useEffect, useMemo, useState } from "react";
import AdminNav from "../components/Admin/AdminNav";
import ColorVariantsEditor from "../components/Admin/ColorVariantsEditor";
import DesignersEditor from "../components/Admin/DesignersEditor";
import { API_URL, PRODUCTS_URL } from "../config";
import { authHeader } from "../auth";
import styles from "./adminDashboard.module.css";

const cloudinaryCloudName = "dk1nepgzk";

const EMPTY_FORM = {
  name: "",
  brand: "",
  price: "",
  category: "",
  productCollection: "",
  gender: "",
  active: true,
  stockInCount: "",
  description: "",
  richDescription: "",
  rating: "0",
  numReviews: "0",
  isFeatured: false,
  outOfStock: false,
};

const AdminDashboard = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [hasColors, setHasColors] = useState(false);
  const [colors, setColors] = useState([]);
  const [colorsUploading, setColorsUploading] = useState(false);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // Real categories and existing brand names come from the backend
  useEffect(() => {
    fetch(`${API_URL}/categories`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => Array.isArray(list) && setCategories(list))
      .catch(() => setError("Could not load categories — is the backend running?"));

    fetch(`${API_URL}/collections`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => Array.isArray(list) && setCollections(list))
      .catch(() => {});

  }, []);

  const setField = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleGalleryAdd = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setGalleryFiles((prev) => [...prev, ...files]);
    setGalleryPreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
    e.target.value = ""; // allow re-selecting the same file later
  };

  const removeGalleryImage = (index) => {
    setGalleryFiles((prev) => prev.filter((_, i) => i !== index));
    setGalleryPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "drxz9h8c");

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudinaryCloudName}/image/upload`,
      { method: "POST", body: formData }
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error?.message || "Image upload failed");
    }
    return data.secure_url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");

    if (!coverFile) {
      setError("A cover image is required.");
      return;
    }
    if (!form.category) {
      setError("Pick a category.");
      return;
    }

    setSaving(true);
    try {
      const image = await uploadToCloudinary(coverFile);
      const images = galleryFiles.length
        ? await Promise.all(galleryFiles.map(uploadToCloudinary))
        : [];

      const payload = {
        ...form,
        productCollection: form.productCollection || undefined,
        designers: designers.filter((d) => d.name.trim()),
        // a named colour counts even without photos (swatch-only)
        colors: hasColors ? colors.filter((c) => c.name.trim()) : [],
        price: Number(form.price),
        stockInCount: Number(form.stockInCount),
        rating: Number(form.rating) || 0,
        numReviews: Number(form.numReviews) || 0,
        image,
        images,
      };

      const response = await fetch(PRODUCTS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        let message;
        try {
          message = JSON.parse(text).message;
        } catch {
          message = text;
        }
        throw new Error(message || "There was an error adding the product.");
      }

      const created = await response.json();
      setNotice(`“${created.name}” added to the store.`);
      setForm(EMPTY_FORM);
      setDesigners([]);
      setHasColors(false);
      setColors([]);
      setCoverFile(null);
      setCoverPreview("");
      setGalleryFiles([]);
      setGalleryPreviews([]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ id: c._id || c.id, name: c.name })),
    [categories]
  );

  return (
    <div className={styles.page}>
      <AdminNav />
      <div className={styles.inner}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Backoffice</span>
          <h1 className={styles.title}>Add a Product</h1>
          <p className={styles.subtitle}>
            Images upload to Cloudinary; the product is saved to the store database.
          </p>
        </header>

        {error && <p className={styles.bannerError}>{error}</p>}
        {notice && <p className={styles.bannerSuccess}>{notice}</p>}

        <form onSubmit={handleSubmit}>
          <div className={styles.layout}>
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Images</h2>

              <label className={coverPreview ? styles.uploadTileFilled : styles.uploadTile}>
                {coverPreview ? (
                  <>
                    <img src={coverPreview} alt="Cover preview" className={styles.coverPreview} />
                    <span className={styles.coverBadge}>Cover — click to replace</span>
                  </>
                ) : (
                  <>
                    <span className={styles.uploadIcon}>＋</span>
                    <span>
                      <strong>Cover image</strong>
                      <br />
                      shown on product cards
                    </span>
                  </>
                )}
                <input type="file" accept="image/*" onChange={handleCoverChange} />
              </label>

              <p className={styles.cardHint}>Gallery — extra angles shown on the product page</p>
              <div className={styles.galleryGrid}>
                {galleryPreviews.map((url, index) => (
                  <div key={url} className={styles.galleryItem}>
                    <img src={url} alt={`Gallery ${index + 1}`} />
                    <button
                      type="button"
                      className={styles.galleryRemove}
                      onClick={() => removeGalleryImage(index)}
                      aria-label={`Remove image ${index + 1}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <label className={styles.galleryAdd}>
                  ＋
                  <input type="file" accept="image/*" multiple onChange={handleGalleryAdd} />
                </label>
              </div>
            </div>

            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Details</h2>
              <div className={styles.fieldGrid}>
                <div className={styles.fieldWide}>
                  <label className={styles.label} htmlFor="p-name">Name</label>
                  <input
                    id="p-name"
                    className={styles.input}
                    required
                    placeholder="LORMAMI BLACK CAMOUFLAGE PANTS"
                    value={form.name}
                    onChange={setField("name")}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="p-category">Category</label>
                  <select
                    id="p-category"
                    className={styles.select}
                    required
                    value={form.category}
                    onChange={setField("category")}
                  >
                    <option value="" disabled>
                      {categoryOptions.length ? "Select a category" : "No categories found"}
                    </option>
                    {categoryOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="p-collection">Collection</label>
                  <select
                    id="p-collection"
                    className={styles.select}
                    value={form.productCollection}
                    onChange={setField("productCollection")}
                  >
                    <option value="">None</option>
                    {collections.map((c) => (
                      <option key={c._id || c.id} value={c._id || c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="p-price">Price (₦)</label>
                  <input
                    id="p-price"
                    className={styles.input}
                    type="number"
                    min="0"
                    step="any"
                    required
                    placeholder="25000"
                    value={form.price}
                    onChange={setField("price")}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="p-stock">Stock count</label>
                  <input
                    id="p-stock"
                    className={styles.input}
                    type="number"
                    min="0"
                    required
                    placeholder="10"
                    value={form.stockInCount}
                    onChange={setField("stockInCount")}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="p-gender">Gender</label>
                  <select
                    id="p-gender"
                    className={styles.select}
                    value={form.gender}
                    onChange={setField("gender")}
                  >
                    <option value="">Unisex / not specified</option>
                    <option value="Men">Men</option>
                    <option value="Women">Women</option>
                  </select>
                </div>

                <div className={styles.fieldWide}>
                  <label className={styles.label}>Designers / Collaborators (optional)</label>
                  <DesignersEditor designers={designers} onChange={setDesigners} />
                </div>

                <div className={styles.fieldWide}>
                  <label className={styles.label} htmlFor="p-desc">Description</label>
                  <textarea
                    id="p-desc"
                    className={styles.textarea}
                    required
                    placeholder="Short description shown in listings"
                    value={form.description}
                    onChange={setField("description")}
                  />
                </div>

                <div className={styles.fieldWide}>
                  <label className={styles.label} htmlFor="p-rich">Rich description</label>
                  <textarea
                    id="p-rich"
                    className={styles.textarea}
                    required
                    placeholder="Full details: fabric, fit, care…"
                    value={form.richDescription}
                    onChange={setField("richDescription")}
                  />
                </div>

                <div className={styles.fieldWide}>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={hasColors}
                      onChange={(e) => {
                        setHasColors(e.target.checked);
                        if (e.target.checked && colors.length === 0) {
                          setColors([{ name: "", hex: "#141210", image: "", images: [] }]);
                        }
                      }}
                    />
                    This piece comes in multiple colours
                  </label>
                  {hasColors && (
                    <ColorVariantsEditor
                      colors={colors}
                      onChange={setColors}
                      onError={setError}
                      onBusyChange={setColorsUploading}
                    />
                  )}
                </div>

                <div className={styles.toggleRow}>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={form.isFeatured}
                      onChange={setField("isFeatured")}
                    />
                    Featured (shows in Best Sellers)
                  </label>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={form.outOfStock}
                      onChange={setField("outOfStock")}
                    />
                    Out of stock
                  </label>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={setField("active")}
                    />
                    Visible on the store
                  </label>
                </div>

                <div className={styles.footer}>
                  <button
                    className={styles.submit}
                    type="submit"
                    disabled={saving || colorsUploading}
                  >
                    {saving
                      ? "Uploading…"
                      : colorsUploading
                      ? "Colour photos uploading…"
                      : "Add Product"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;
